import { Box, Typography } from '@material-ui/core';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';
import {
  AuthenticationModal,
  InlineTextButton,
  NamedLink,
  Product,
  ShopifyOrders,
  ShopifyProducts,
  ShopifyProductSearch,
  TypographyWrapper,
} from '../../components';
import { useShopConfig } from '../../hooks/shopConfig';
import { setAuthModalTrigger } from '../../ducks/Auth.duck';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import SellOrTradeInEducationModal from '../../components/SellOrTradeInEducationModal/SellOrTradeInEducationModal';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { getLocalStorage, setLocalStorage } from '../../util/localStorageHelpers';
import { CurrentUser } from '../../types/sharetribe/currentUser';
import {
  ShopifyProductsSearchFormField,
  shouldFillVariantOption,
} from '../../components/Shopify/Shopify.utils';
import { useAllowedFindItemMethods } from '../../hooks/useAllowedFindItemMethods';
import { parse } from '../../util/urlHelpers';
import { EditListingPageState } from './EditListingPage.duck';
import Heading from './Heading';
import ItemNameForm from './ItemNameForm';
import { getOrderHistoryDescription, ListingPageParamTab } from './EditListingPage.utils';
import { ModalType } from '../../ducks/modal.duck';
import { useActiveModal } from '../../hooks/useActiveModal';
import { fetchShopifyProductVariantWithProductById } from '../../util/api';
import * as log from '../../util/log';
import AppContext from '../../context/AppContext';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { ListingFlowFormField } from './ListingFlowTypes';
import { FindItemMethod } from '../../types/sharetribe/listing';

const SelectedProduct: FC = () => {
  const { values } = useFormState();
  const { shopifyProduct } = values;

  return (
    <>
      <Heading>Selected Item</Heading>
      <Box pt={2}>
        <Product product={shopifyProduct} selectedProductId={shopifyProduct?.id} />
      </Box>
    </>
  );
};

const FindWithOrderNumberLink: FC = () => {
  const { params } = useSelector<any>((state) => state.EditListingPage) as EditListingPageState;

  const { listingFlowConfig, shopName } = useShopConfig();
  const { orderHistoryDescription } = listingFlowConfig;

  // Sometimes the params are empty when the page is loaded.
  if (isEmpty(params)) {
    return null;
  }

  return (
    <TypographyWrapper variant="body2" typographyOverrides={{ display: 'inline' }}>
      Ordered through {shopName} and don’t see your order? Try{' '}
      <NamedLink
        name="EditListingPage"
        params={{ ...params, tab: ListingPageParamTab.OrderNumber }}
      >
        <TypographyWrapper
          variant="body2"
          format={TypographyFormat.Underlined}
          typographyOverrides={{ display: 'inline' }}
        >
          finding via order number
        </TypographyWrapper>
      </NamedLink>
      . {getOrderHistoryDescription(orderHistoryDescription)}
    </TypographyWrapper>
  );
};

const AuthModalLink: FC<{
  triggerName: string | null;
  bannerText: string;
  children: string;
}> = (props) => {
  const { triggerName, bannerText, children } = props;
  const dispatch = useDispatch();
  const { authModalTrigger } = useSelector<any>((state) => state.Auth) as any;

  return (
    <>
      <AuthenticationModal
        open={authModalTrigger !== null}
        handleClose={() => dispatch(setAuthModalTrigger(null))}
        onSuccess={() => dispatch(setAuthModalTrigger(null))}
        bannerText={bannerText}
      />
      <InlineTextButton
        onClick={() => dispatch(setAuthModalTrigger(triggerName))}
        style={{ textDecoration: 'underline' }}
      >
        {children}
      </InlineTextButton>
    </>
  );
};

export const SignUpToSeeOrderHistory: FC<{ shopName: string }> = (props) => {
  const { shopName } = props;
  return (
    <Box my={1}>
      <Typography component="div" variant="body1">
        <AuthModalLink
          bannerText={`Sign up or log in to see your ${shopName} order history.`}
          triggerName="auth-modal-view-orders-trigger"
        >
          Sign up or log in
        </AuthModalLink>{' '}
        to select an item from your {shopName} order history.
      </Typography>
    </Box>
  );
};

export const EmailVerificationGate: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { sendVerificationEmailInProgress, currentUser } = useSelector<any>(
    (state) => state.user
  ) as any;
  const dispatch = useDispatch();
  const [hasClickedEmailVerification, setHasClickedEmailVerification] = useState(false);

  const handleEmailVerificationClick = () => {
    setHasClickedEmailVerification(true);
    return dispatch(sendVerificationEmail());
  };

  const isCurrentUserVerified = currentUser?.attributes?.emailVerified;

  return (
    <>
      {!isCurrentUserVerified && (
        <Box my={1}>
          <TypographyWrapper
            variant="body1"
            typographyOverrides={{ display: 'inline', style: { marginRight: '3px' } }}
          >
            Please verify your email to view your order history.
          </TypographyWrapper>
          {hasClickedEmailVerification ? (
            <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
              {sendVerificationEmailInProgress ? 'Sending...' : 'Email verification sent!'}
            </TypographyWrapper>
          ) : (
            <InlineTextButton
              typographyFormat={TypographyFormat.Underlined}
              onClick={handleEmailVerificationClick}
            >
              Resend verification email.
            </InlineTextButton>
          )}
        </Box>
      )}
      {isCurrentUserVerified && children}
    </>
  );
};

const YourOrders: FC = () => {
  const { shopName, listingFlowConfig } = useShopConfig();
  const { allowedFindItemMethods } = listingFlowConfig;
  const currentUser = useSelector<any>((state) => state.user.currentUser) as
    | CurrentUser
    | undefined;

  return (
    <>
      <Heading>Your {shopName} Orders</Heading>
      {currentUser && (
        <EmailVerificationGate>
          <>
            {allowedFindItemMethods.includes(FindItemMethod.OrderNumber) && (
              <Box py={1}>
                <FindWithOrderNumberLink />
              </Box>
            )}
            {allowedFindItemMethods.includes(FindItemMethod.EmailAccount) && (
              <Box pt={1}>
                <ShopifyOrders />
              </Box>
            )}
          </>
        </EmailVerificationGate>
      )}

      {!currentUser && <SignUpToSeeOrderHistory shopName={shopName} />}
    </>
  );
};

const OtherItems: FC = () => {
  const { shopName } = useShopConfig();
  return (
    <>
      <Heading>Other {shopName} Items</Heading>
      <Box py={1}>
        <ShopifyProducts />
      </Box>
    </>
  );
};

const EnterManually: FC = () => {
  const form = useForm();
  const handleNameChange = () => form.change('findItemMethod', FindItemMethod.CannotFind);

  return (
    <>
      <Heading>Enter Your Item Manually</Heading>
      <Box py={1}>
        <ItemNameForm onChange={handleNameChange} />
      </Box>
    </>
  );
};

const Search: FC<{ shouldHideTitle?: boolean }> = (props) => {
  const { shouldHideTitle } = props;

  const shopConfig = useShopConfig();
  const { treetId } = useContext(AppContext);
  const { countryCode } = useBrandCountryConfig();
  const { allowTradeInAndMarketplace } = useEnabledCustomerExperiences();
  const { isTradeInAdmin } = useCurrentUserPermissions();
  const { shouldAllowSearch, shouldAllowCannotFind } = useAllowedFindItemMethods();

  const location = useLocation();
  const { productVariantId } = parse(location.search);

  const { listingFlowConfig, shouldShowOtherItemsInSearch, shopifyDomain } = shopConfig;
  const { allowedFindItemMethods, findItemDescription } = listingFlowConfig;
  const shouldNotShowOrders =
    isEmpty(shopifyDomain) ||
    (!allowedFindItemMethods.includes(FindItemMethod.EmailAccount) &&
      !allowedFindItemMethods.includes(FindItemMethod.OrderNumber));

  const form = useForm();
  const { initialValues, values } = useFormState();
  const { findItemMethod, shopifyProduct } = values;

  const {
    isModalOpen: isSellOrTradeInEducationalModalOpen,
    openModal: openSellOrTradeInEducationalModal,
    closeModal: closeSellOrTradeInEducationalModal,
  } = useActiveModal(ModalType.SellOrTradeInEducational);

  useEffect(() => {
    const fetchProductVariant = async () => {
      if (productVariantId) {
        try {
          const response = await fetchShopifyProductVariantWithProductById({
            id: productVariantId,
            treetId,
            countryCode,
          });

          const productVariant = response?.data?.productVariant;

          if (productVariant?.product) {
            const itemVariantOptions = productVariant.selectedOptions.reduce(
              (acc: { [option: string]: string }, option: any) => {
                if (shouldFillVariantOption(option, shopConfig)) {
                  return { ...acc, [option.name.toLowerCase()]: option.value };
                }
                return acc;
              },
              {}
            );
            form.change(ListingFlowFormField.ShopifyProduct, productVariant.product);
            form.change(ListingFlowFormField.ShopifyProductVariant, productVariant);
            form.change(ListingFlowFormField.SelectedVariantOptions, itemVariantOptions);
          }
        } catch (e) {
          log.error(e, 'fetch-shopify-product-variant-with-product-by-id-failed', {
            productVariantId,
          });
        }
      }
    };

    fetchProductVariant();
  }, [productVariantId]);

  useEffect(() => {
    if (
      allowTradeInAndMarketplace &&
      !getLocalStorage('hasSeenTradeInOrListInfoPage') &&
      !isTradeInAdmin
    ) {
      openSellOrTradeInEducationalModal();
    }
  }, []);

  const isSearchActive = values[ShopifyProductsSearchFormField.IsSearchActive];

  const isSelectedFromSubpage =
    findItemMethod === FindItemMethod.OrderNumber || findItemMethod === FindItemMethod.Search;
  const hasNavigatedPage = !!initialValues.shopifyProduct;
  const shouldShowSelectedProduct =
    !isSearchActive && !!shopifyProduct && (isSelectedFromSubpage || hasNavigatedPage);

  const actionTitle = allowTradeInAndMarketplace ? (
    <InlineTextButton onClick={openSellOrTradeInEducationalModal}>
      <TypographyWrapper variant="h1" format={TypographyFormat.Underlined}>
        selling or trading in
      </TypographyWrapper>
    </InlineTextButton>
  ) : (
    'selling'
  );

  return (
    <>
      <SellOrTradeInEducationModal
        isOpen={isSellOrTradeInEducationalModalOpen}
        onClose={() => {
          closeSellOrTradeInEducationalModal();
          setLocalStorage('hasSeenTradeInOrListInfoPage', 'true');
        }}
      />
      {!shouldHideTitle && <Typography variant="h1">What are you {actionTitle}?</Typography>}
      {!!findItemDescription && (
        <Box pt={1}>
          <Typography variant="body1">{findItemDescription}</Typography>
        </Box>
      )}
      {/* TODO (TREET-1910): Refactor <ShopifyProducts> and ShopifyProductSearch to just use one component */}
      {shouldAllowSearch && (
        <Box pt={3}>
          <ShopifyProductSearch />
        </Box>
      )}
      {shouldShowSelectedProduct && <SelectedProduct />}
      {!isSearchActive && (
        <>
          {!shouldNotShowOrders && <YourOrders />}
          <Box pb={6}>
            {shouldAllowSearch && shouldShowOtherItemsInSearch && <OtherItems />}
            {!shouldAllowSearch && shouldAllowCannotFind && <EnterManually />}
          </Box>
        </>
      )}
    </>
  );
};

export default Search;
